import { useAppScopeStore } from '@frontend/scope';
import { useListProvidersMultiQuery, useListProvidersQuery } from '../queries';
import { ListProvidersIO, ListProvidersMultiIO, PageConfig } from '../types';
import { useGetSchedulerV3FeatureFlagDetails } from './use-get-scheduler-v3-feature-flag-details';

type UseGetProvidersV3DataParams = {
  selectedLocationId: string;
  selectedLocationIds?: string[];
  debouncedSearchValue?: string;
  pageConfig?: PageConfig;
  forCalendarView?: boolean;
  isEnabled?: boolean;
};

export const useGetProvidersV3Data = ({
  selectedLocationId,
  selectedLocationIds,
  debouncedSearchValue = '',
  pageConfig,
  forCalendarView = false,
  isEnabled = true,
}: UseGetProvidersV3DataParams) => {
  const { isScheduleV3FlagEnabled } = useGetSchedulerV3FeatureFlagDetails();
  const { selectedGroupId, isSingleTypeScope } = useAppScopeStore();
  const parentLocationId = selectedGroupId;
  const isMultiLocation = !isSingleTypeScope;
  const isMultiLocationApiCall = !!parentLocationId && isMultiLocation;
  const request: ListProvidersIO['input'] = {
    locationId: selectedLocationId,
    showHidden: !forCalendarView,
    ...(pageConfig?.limit && { limit: pageConfig?.limit }),
    ...(pageConfig?.page && { page: pageConfig?.page }),
    ...(debouncedSearchValue && { name: debouncedSearchValue }),
  };

  const singleLocationGetProvidersQuery = useListProvidersQuery({
    request: request,
    options: {
      enabled: !isMultiLocationApiCall && isEnabled && isScheduleV3FlagEnabled && !!request.locationId,
    },
  });

  const requestForMulti: ListProvidersMultiIO['input'] = {
    ...(pageConfig?.limit && { limit: pageConfig?.limit }),
    ...(pageConfig?.page && { page: pageConfig?.page }),
    ...(debouncedSearchValue && { name: debouncedSearchValue }),
    locationIds: (selectedLocationIds || (selectedLocationId ? [selectedLocationId] : [])).sort(),
    parentLocationId,
    forCalendarView,
  };

  const multiLocationGetProvidersQuery = useListProvidersMultiQuery({
    request: requestForMulti,
    options: {
      enabled:
        isMultiLocationApiCall &&
        isEnabled &&
        isScheduleV3FlagEnabled &&
        !!requestForMulti.parentLocationId &&
        !!requestForMulti.locationIds.length,
    },
  });

  return isMultiLocationApiCall ? multiLocationGetProvidersQuery : singleLocationGetProvidersQuery;
};
